import { LucideIcon } from 'lucide-react';
import { Button } from "@/shared/ui/button";
import { cn } from '@/shared/utils/date-utils';

interface EmptyViewProps {
  icon: LucideIcon;
  title: string;
  description: string;
  size?: 'small' | 'medium' | 'large';
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}

const SIZE_STYLES = {
  small: {
    container: 'p-4 gap-2',
    content: 'gap-1.5',
    icon: 'h-6 w-6',
    title: 'text-sm font-semibold',
    description: 'text-xs text-muted-foreground',
    button: 'h-7 text-xs px-3',
  },
  medium: {
    container: 'p-6 gap-3',
    content: 'gap-2',
    icon: 'h-10 w-10',
    title: 'text-base font-semibold',
    description: 'text-sm text-muted-foreground',
    button: 'h-8 text-sm px-4',
  },
  large: {
    container: 'p-8 gap-4',
    content: 'gap-2.5',
    icon: 'h-14 w-14',
    title: 'text-lg font-semibold',
    description: 'text-base text-muted-foreground',
    button: 'h-9 text-sm px-5',
  },
} as const;

const EmptyView = ({
  icon: Icon,
  title,
  description,
  size = 'large',
  actionLabel,
  onAction,
  className,
}: EmptyViewProps) => {
  const styles = SIZE_STYLES[size];

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center w-full text-center bg-transparent',
        styles.container,
        className,
      )}
    >
      <div className={cn('flex flex-col items-center', styles.content)}>
        <Icon className={cn('shrink-0 text-muted-foreground', styles.icon)} />
        <div>
          <p className={styles.title}>{title}</p>
          <p className={styles.description}>{description}</p>
        </div>
      </div>
      {actionLabel && onAction && (
        <Button onClick={onAction} variant="default" className={styles.button}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export default EmptyView;
